import Guid from '@Common/Core/Guid';
import { TypeKind } from '@Common/Enums';
import documentHelper from '@Common/Helpers/DocumentHelper';
import SeriesHelper from '@Common/Helpers/SeriesHelper';
import { BaseTypedDefinition, RecordData, RecordDefinition, RecordSeries } from '@InstanceFactory';
import { TypedValue, TypedValueArray, TypedValuePrimitive } from './TypeGuard';

type ISeries = RecordSeries;

abstract class TypeSpecifier<T> {
    constructor(protected projectId: string) {}

    public abstract specify(data: T): void;

    public abstract canSpecify(data: T): boolean;
}

export class TypeSpecifierAggregator extends TypeSpecifier<unknown> {
    private readonly specifiers: TypeSpecifier<unknown>[];

    constructor(projectId: string) {
        super(projectId);
        this.specifiers = [new RecordSeriesTypeSpecifier(this.projectId), new RecordsDataTypeSpecifier(this.projectId)];
    }

    public canSpecify(data: unknown): boolean {
        return data && this.specifiers.some(s => s.canSpecify(data));
    }

    public specify(data: unknown): void {
        const specifier = this.specifiers.filter(s => s.canSpecify(data))[0];
        specifier && specifier.specify(data);
    }
}

class RecordsDataTypeSpecifier extends TypeSpecifier<RecordData> {
    public specify(recordData: RecordData): void {
        const record: RecordDefinition = documentHelper.findRecord(this.projectId, { id: recordData.recordId });
        if (!record) {
            return;
        }
        const fields = record.fields.searchTree(f => f.id);
        const cells = recordData.recordRowCells.filter(cell => fields[cell.id] && cell.value);
        for (const cell of cells) {
            const field = fields[cell.id];
            const seriesType = RecordHelper.getFieldType(field);
            if (seriesType !== null) {
                cell.value.rawData = specifyType(seriesType, cell.value.rawData);
            }
        }
    }

    private static readonly isRecordData = _.conforms<Partial<RecordData>>({
        recordId: _.stubTrue,
        recordRowCells: _.isArray
    });

    public canSpecify(data: Partial<RecordData>): boolean {
        return RecordsDataTypeSpecifier.isRecordData(data);
    }
}

class RecordSeriesTypeSpecifier extends TypeSpecifier<ISeries> {
    private readonly seriesHelper: SeriesHelper;

    constructor(projectId: string) {
        super(projectId);
        this.seriesHelper = new SeriesHelper(this.projectId);
    }

    public specify(series: ISeries): void {
        const filterItem = series.dataDefinition.filterItem;
        if (filterItem && filterItem.values && filterItem.values.length) {
            const seriesType = this.seriesHelper.getSeriesType(series);
            if (seriesType) {
                filterItem.values = specifyType(seriesType, filterItem.values) as any;
            }
        }
    }

    public canSpecify(data: ISeries): boolean {
        return (
            (SeriesHelper.isRecordSeries(data) || SeriesHelper.isTTRecordSeries(data)) &&
            data.dataDefinition != null &&
            data.dataDefinition.filterItem != null
        );
    }
}

class RecordHelper {
    public static getFieldType(field: BaseTypedDefinition): TypeKind | null {
        if (!field) {
            return null;
        }

        if (field.ttId !== Guid.empty) {
            return TypeKind.string;
        }

        return field.type as TypeKind;
    }
}

const arraySuffix = '[]';

function isSpecifiedType(value: any): boolean {
    return value != null && _.has(value, '$type');
}

type SpecifiableTypes = keyof typeof typeKindToServerType;

function isSpecifiableType(type: TypeKind): type is SpecifiableTypes {
    return type in typeKindToServerType;
}

export function specifyByteType(value: any): TypedValuePrimitive {
    return { $type: typeKindToServerType[TypeKind.binary].serverType + arraySuffix, $value: value };
}

export function specifyType(typeKind: TypeKind, value: any[]): TypedValueArray;
export function specifyType(typeKind: 'Binary', value: any): TypedValueArray;
export function specifyType(typeKind: TypeKind, value: any): TypedValue;
export function specifyType(typeKind: TypeKind, value: any | any[]): TypedValue {
    if (value == null || !isSpecifiableType(typeKind) || isSpecifiedType(value)) {
        return value;
    }

    const type = typeKindToServerType[typeKind];

    if (_.isArray(value) || type.isArray) {
        return { $type: type.serverType + arraySuffix, $values: value };
    } else {
        return { $type: type.serverType, $value: value };
    }
}

// TODO: to receive information from the server about this specification
export const typeKindToServerType = {
    [TypeKind.float]: { serverType: 'System.Double', isArray: false },
    [TypeKind.int]: { serverType: 'System.Int32', isArray: false },
    [TypeKind.guid]: { serverType: 'System.Guid', isArray: false },
    [TypeKind.bool]: { serverType: 'System.Boolean', isArray: false },
    [TypeKind.binary]: { serverType: 'System.Byte', isArray: true }
} as const;
